'use client'

import { useEffect } from 'react'
import { parseHostname, validateCurrentSitename } from '@/utilities/sitename'

// Client-only redirect guard for public pages
// Rules:
// - Suppress redirection on *.azurewebsites.net (always allow)
// - Allow localhost, but can disable redirects via NEXT_PUBLIC_DISABLE_LOCALHOST_REDIRECTS
// - On subdomain hosts, if sitename is invalid, redirect to base domain preserving path & query
// - Only runs on client so Azure healthchecks are unaffected
export default function SitenameGate({
  dynamicUserSqueezePagesEnabled = true,
}: {
  dynamicUserSqueezePagesEnabled?: boolean
}) {
  useEffect(() => {
    const run = async () => {
      try {
        const host = window.location.hostname
        const { isAzureWebsites, isLocalhost, baseDomain, candidate } = parseHostname(host)

        // Suppress on Azure *.azurewebsites.net per requirement
        if (isAzureWebsites) return

        // Localhost: allow redirect unless disabled via env
        if (isLocalhost) {
          const disableLocal = String(
            process.env.NEXT_PUBLIC_DISABLE_LOCALHOST_REDIRECTS || '',
          ).toLowerCase()
          const disabled = disableLocal === '1' || disableLocal === 'true' || disableLocal === 'yes'
          if (disabled) return
        }

        // Only enforce for front-facing routes we are mounted on
        const pathname = window.location.pathname
        const publicPrefixes = new Set(['/', '/pricing'])
        const isPublic = pathname === '/' || pathname.startsWith('/pricing') || true // pages under CMS are in this layout
        if (!isPublic) return

        // No candidate means base domain — nothing to do
        if (!candidate) return

        // Feature toggle: if dynamic user squeeze pages are disabled, always collapse to base domain
        if (!dynamicUserSqueezePagesEnabled) {
          const url = new URL(window.location.href)
          if (baseDomain) {
            url.hostname = baseDomain
            window.location.replace(url.toString())
          }
          return
        }

        const { validation } = await validateCurrentSitename()
        const valid =
          validation.ok &&
          (validation.status === 'pending' ||
            validation.status === 'active' ||
            validation.status === 'approved')

        if (!valid) {
          // Redirect to base domain, preserving path and query
          const url = new URL(window.location.href)
          if (baseDomain) {
            url.hostname = baseDomain
            window.location.replace(url.toString())
          }
        }
      } catch (e) {
        // Fail-open: do nothing
      }
    }
    run()
  }, [])
  return null
}
